import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../login/logincss/Login.css";
import logo from "../../images/auravit_logo.png";
import { userService } from "../../services/firestoreService";
import { auth, db } from "../../firebase";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { serverTimestamp, doc, updateDoc } from "firebase/firestore";
import {
  clearPendingAccountReactivation,
  clearStoredLoginOtpChallenge,
  finalizePendingAccountReactivation,
  getPendingAccountReactivation,
  refreshStoredLoginOtpChallenge,
  verifyStoredLoginOtp,
} from "../../services/authFlowService";
import { createOtpRequestId, generateOtpCode, sendOtpEmail, sendOtpSms } from "../../services/otpService";
import {
  clearStoredRegistrationPassword,
  getStoredRegistrationPassword,
  hashPassword,
} from "../../services/passwordUtils";
import AppHeader from "../../components/AppHeader";
import { sessionService } from "../../services/sessionService";

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
  );
}

export default function UserVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const verificationType = location.state?.type || "login";
  const OTP_LENGTH = 6;

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [resendText, setResendText] = useState("Resend");
  const [otpExpired, setOtpExpired] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const inputsRef = useRef([]);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [verificationStep, setVerificationStep] = useState("user");
  const [verificationMethod, setVerificationMethod] = useState("email");
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [userPhone, setUserPhone] = useState(null);
  const [timerActive, setTimerActive] = useState(true);
  const [cameFromSmsMethod, setCameFromSmsMethod] = useState(false);
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);

  const getTimerState = () => {
    if (otpExpired) return "expired";
    if (timeLeft <= 5) return "critical";
    if (timeLeft <= 15) return "warning";
    return "normal";
  };

  const getCardClasses = () => {
    const baseClass = "forgot-card";
    const timerState = getTimerState();
    
    if (timerState === "expired") return `${baseClass} timer-expired`;
    if (timerState === "critical") return `${baseClass} timer-critical timer-active`;
    if (timeLeft > 0) return `${baseClass} timer-active`;
    return baseClass;
  };

  const getTimerClasses = () => {
    const timerState = getTimerState();
    return `timer timer-${timerState}`;
  };

  const draft = JSON.parse(localStorage.getItem("registrationDraft") || "null");
  const isMinor = draft?.assistanceRequired === "yes" && 
    (Array.isArray(draft?.reasonForAssistance) 
      ? draft?.reasonForAssistance.includes("minor") 
      : draft?.reasonForAssistance === "minor");

  const emailDisplay = verificationType === "login" 
    ? (location.state?.otpEmail || location.state?.email || "your registered email")
    : draft?.email;

  const normalizePhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, '');
    
    if (cleaned.length === 10 && cleaned.startsWith('9')) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith('63')) {
      return `+${cleaned}`;
    } else if (phone.startsWith('+63')) {
      return phone;
    }
    return null;
  };

  useEffect(() => {
    let phone = null;
    if (verificationType === "login" && location.state?.user) {
      const user = location.state.user;
      const rawPhone = user.phone || user.contact || user.contactDetails?.contact || (user.phoneLast10 ? `+63${user.phoneLast10}` : null);
      phone = normalizePhoneNumber(rawPhone);
    } else if (draft) {
      const rawPhone = draft.phone || draft.contact || draft.contactDetails?.contact || (draft.phoneLast10 ? `+63${draft.phoneLast10}` : null);
      phone = normalizePhoneNumber(rawPhone);
    }
    setUserPhone(phone);
  }, [verificationType, location.state, draft]);

  useEffect(() => {
    if (!timerActive || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setOtpExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, timerActive]);

  useEffect(() => {
    if (verificationType !== "registration") return;

    const draft = JSON.parse(localStorage.getItem("registrationDraft") || "null");
    if (!draft || !draft.email) {
      setError("Registration data missing. Please restart registration.");
      return;
    }

    if (draft.registrationOtp && draft.registrationOtpExpiresAt && Date.now() < draft.registrationOtpExpiresAt) {
      return;
    }

    const sendInitialOtp = async () => {
      try {
        setIsSendingVerification(true);
        
        const newOtp = generateOtpCode();
        const requestId = createOtpRequestId("registration");
        const updatedDraft = {
          ...draft,
          registrationOtp: newOtp,
          registrationOtpExpiresAt: Date.now() + 60 * 1000,
          registrationOtpVerified: false,
          registrationOtpRequestId: requestId,
        };
        localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

        await sendOtpEmail({
          email: draft.email,
          otp: newOtp,
          purpose: "registration",
          requestId,
          expiresAt: updatedDraft.registrationOtpExpiresAt,
        });
      } catch (e) {
        console.error("Initial OTP send error", e);
        setError("Unable to send verification code. Please try resending.");
      } finally {
        setIsSendingVerification(false);
      }
    };

    sendInitialOtp();
  }, [verificationType]); // Note: draft is read from localStorage, so dependency on verificationType is sufficient

  const resetOtpExpiration = () => {
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
  };

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });

      setOtp(newOtp);
      setError("");
      const focusIndex = Math.min(pasteArray.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex].focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (!isOtpComplete) {
      setError("Please enter the full 6-digit code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    if (otpExpired) {
      setError("OTP expired. Please request a new code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    try {
      if (verificationType === "login") {
        const result = verifyStoredLoginOtp(otp.join(""));
        if (!result.success) {
          setError(result.error);
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        clearStoredLoginOtpChallenge();

        const pendingReactivation = getPendingAccountReactivation();
        if (pendingReactivation?.uid || pendingReactivation?.email) {
          try {
            await finalizePendingAccountReactivation({
              uid: pendingReactivation?.uid || auth.currentUser?.uid,
              email: pendingReactivation?.email || auth.currentUser?.email,
              userName: pendingReactivation?.userName || "User",
              db,
              apiBase: process.env.REACT_APP_FUNCTIONS_URL || "https://us-central1-auravit-bb843.cloudfunctions.net",
            });
          } catch (error) {
            console.error("Error finalizing pending reactivation after login verification:", error);
          }
        }

        // Update lastLogin timestamp after successful OTP verification
        try {
          const user = auth.currentUser;
          if (user) {
            await updateDoc(doc(db, 'users', user.uid), {
              lastLogin: serverTimestamp()
            });
          }
        } catch (error) {
          console.error("Error updating lastLogin after OTP verification:", error);
          // Non-fatal error, continue with login flow
        }

        const stored = JSON.parse(localStorage.getItem("user") || "{}");

        // Create a session record for this browser/device
        if (stored.uid) {
          await sessionService.trackSession(stored.uid);
          // Clean up old revoked sessions in the background (non-blocking)
          sessionService.pruneOldSessions(stored.uid).catch(() => {});
        }

        navigate("/user-home", { state: { user: stored } });
        sessionStorage.removeItem('freshLoginInProgress');
        return;
      }

      const registrationPassword = getStoredRegistrationPassword();
      if (!draft || !draft.email || !registrationPassword) {
        setError("Registration data missing. Please restart registration.");
        return;
      }

      const code = otp.join("");
      if (String(code) !== String(draft.registrationOtp)) {
        setError("Incorrect code, please try again");
        setOtp(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        return;
      }

      if (Date.now() > draft.registrationOtpExpiresAt) {
        setError("OTP expired. Please request a new one.");
        setOtp(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        return;
      }

      const cred = await createUserWithEmailAndPassword(
        auth,
        draft.email.trim().toLowerCase(),
        registrationPassword
      );
      const phoneLast10 = draft.phoneLast10 || draft.contact || draft.phoneNumber || "";
      const phone = draft.phone || draft.contact || draft.phoneNumber || (phoneLast10 ? `+63${phoneLast10}` : "");
      
      const hashedPassword = await hashPassword(registrationPassword);

      const flatUserData = {
        uid: cred.user.uid,
        email: draft.email,
        username: draft.username || "",
        password: hashedPassword,
        firstName: draft.firstName,
        lastName: draft.lastName,
        dob: draft.dob,
        sex: draft.sex,
        address: draft.address || "",
        region: draft.region || "",
        province: draft.province || "",
        city: draft.city || "",
        barangay: draft.barangay || "",
        contact: draft.contact || draft.phoneLast10 || draft.phone || "",
        phone: draft.phone || "",
        phoneLast10: draft.phoneLast10 || "",
        uniqueIdentifierCode: draft.uniqueIdentifierCode || "",
        registrationStep: draft.registrationStep || 8,
        assistanceRequired: draft.assistanceRequired || "no",
        reasonForAssistance: draft.reasonForAssistance || [],
        disabilityText: draft.disabilityText || "",
        reasonText: draft.reasonText || "",
        guardianFullName: draft.guardianFullName || "",
        guardianRelationshipToUser: draft.guardianRelationshipToUser || "",
        guardianContactNumber: draft.guardianContactNumber || "",
        guardianAddress: draft.guardianAddress || "",
        accompanistFullName: draft.accompanistFullName || "",
        accompanistRelationshipToUser: draft.accompanistRelationshipToUser || "",
        accompanistContactNumber: draft.accompanistContactNumber || "",
        accompanistAddress: draft.accompanistAddress || "",
        emergencyContact: draft.emergencyContact || {},
        verified: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        twoFactorEnabled: true,
        notificationSettings: {
          educational: true,
          events: true,
          announcements: true,
          appointments: true,
          dataPrivacy: true,
        },
        notificationTimestamps: {
          educationalEnabledAt: serverTimestamp(),
          eventsEnabledAt: serverTimestamp(),
          announcementsEnabledAt: serverTimestamp(),
          appointmentsEnabledAt: serverTimestamp(),
          dataPrivacyEnabledAt: serverTimestamp(),
        }
      };

      await userService.saveUserWithGroupedStructure(cred.user.uid, flatUserData);
      const finalUser = { uid: cred.user.uid, ...flatUserData };
      localStorage.setItem("user", JSON.stringify(finalUser));
      localStorage.removeItem("registrationDraft");
      clearStoredRegistrationPassword();

      navigate("/user-home", { state: { user: finalUser, fromSignup: true } });
    } catch (err) {
      console.error("OTP verify error:", err);
      setError(err.message || "Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendText === "Sent!" || isSendingVerification) return;

    try {
      setIsSendingVerification(true);

      if (verificationMethod === "email") {
        if (verificationType === "login") {
          const refreshedChallenge = refreshStoredLoginOtpChallenge();
          const deliveryEmail =
            refreshedChallenge.otpDeliveryEmail || refreshedChallenge.email;
          await sendOtpEmail({
            email: deliveryEmail,
            otp: refreshedChallenge.otp,
            purpose: "login",
            requestId: refreshedChallenge.requestId,
            expiresAt: refreshedChallenge.expiresAt,
          });
        } else {
          const draft = JSON.parse(localStorage.getItem("registrationDraft") || "null");
          if (!draft || !draft.email) {
            setError("Unable to resend OTP. Registration draft missing.");
            return;
          }

          const deliveryEmail = draft.email;
          if (!deliveryEmail) {
            setError("Unable to resend OTP. Target email missing.");
            return;
          }

          const newOtp = generateOtpCode();
          const requestId = createOtpRequestId("registration");
          const updatedDraft = {
            ...draft,
            registrationOtp: newOtp,
            registrationOtpExpiresAt: Date.now() + 60 * 1000,
            registrationOtpVerified: false,
            registrationOtpRequestId: requestId,
          };
          localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

          await sendOtpEmail({
            email: deliveryEmail,
            otp: newOtp,
            purpose: "registration",
            requestId,
            expiresAt: updatedDraft.registrationOtpExpiresAt,
          });
        }
      } else if (verificationMethod === "sms" && userPhone) {
        if (verificationType === "login") {
          const refreshedChallenge = refreshStoredLoginOtpChallenge();
          await sendOtpSms({
            phone: userPhone,
            otp: refreshedChallenge.otp,
            purpose: "login",
            requestId: createOtpRequestId("login-sms"),
            expiresAt: refreshedChallenge.expiresAt,
          });
        } else {
          const draft = JSON.parse(localStorage.getItem("registrationDraft") || "null");
          if (!draft) {
            setError("Unable to resend OTP. Registration draft missing.");
            return;
          }

          const newOtp = generateOtpCode();
          const requestId = createOtpRequestId("registration-sms");
          const updatedDraft = {
            ...draft,
            registrationOtp: newOtp,
            registrationOtpExpiresAt: Date.now() + 60 * 1000,
            registrationOtpVerified: false,
            registrationOtpRequestId: requestId,
          };
          localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

          await sendOtpSms({
            phone: userPhone,
            otp: newOtp,
            purpose: "registration",
            requestId,
            expiresAt: updatedDraft.registrationOtpExpiresAt,
          });
        }
      }

      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      resetOtpExpiration();
      setResendText("Sent!");
      if (verificationMethod === "sms") {
        setHasSmsBeenSent(true);
      }
      setTimeout(() => setResendText("Resend"), 3000);
    } catch (e) {
      console.error("Resend OTP error", e);
      setError("Unable to resend OTP at the moment.");
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleTryAnotherWay = () => {
    setVerificationMethod("sms");
    setHasSmsBeenSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setCameFromSmsMethod(false);
  };

  const handleTryAnotherWayBack = () => {
    setVerificationMethod("email");
    setHasSmsBeenSent(false);
    setHasEmailBeenSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setCameFromSmsMethod(true);
  };

  const handleSendEmail = async () => {
    if (isSendingVerification) return;

    try {
      setIsSendingVerification(true);

      if (verificationType === "login") {
        const email = location.state?.email || location.state?.otpEmail || "your registered email";
        const newOtp = generateOtpCode();
        const requestId = createOtpRequestId();
        const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

        await refreshStoredLoginOtpChallenge({
          otp: newOtp,
          expiresAt,
          requestId,
        });

        await sendOtpEmail({
          email,
          otp: newOtp,
          purpose: "login",
          requestId,
          expiresAt,
        });
      } else if (verificationType === "registration") {
        const newOtp = generateOtpCode();
        const requestId = createOtpRequestId();
        const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

        const updatedDraft = {
          ...draft,
          registrationOtp: newOtp,
          registrationOtpRequestId: requestId,
          registrationOtpExpiresAt: expiresAt,
        };
        localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

        await sendOtpEmail({
          email: draft.email,
          otp: newOtp,
          purpose: "registration",
          requestId,
          expiresAt,
        });
      }

      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
      setTimerActive(true);
      setHasEmailBeenSent(true);
    } catch (e) {
      console.error("Send Email OTP error", e);
      setError("Unable to send OTP email at the moment.");
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleSendSms = async () => {
    if (isSendingVerification) return;

    try {
      setIsSendingVerification(true);

      if (verificationType === "login") {
        const refreshedChallenge = refreshStoredLoginOtpChallenge();
        await sendOtpSms({
          phone: userPhone,
          otp: refreshedChallenge.otp,
          purpose: "login",
          requestId: createOtpRequestId("login-sms"),
          expiresAt: refreshedChallenge.expiresAt,
        });
      } else {
        const draft = JSON.parse(localStorage.getItem("registrationDraft") || "null");
        if (!draft) {
          setError("Unable to send OTP. Registration draft missing.");
          return;
        }

        const newOtp = generateOtpCode();
        const requestId = createOtpRequestId("registration-sms");
        const updatedDraft = {
          ...draft,
          registrationOtp: newOtp,
          registrationOtpExpiresAt: Date.now() + 60 * 1000,
          registrationOtpVerified: false,
          registrationOtpRequestId: requestId,
        };
        localStorage.setItem("registrationDraft", JSON.stringify(updatedDraft));

        await sendOtpSms({
          phone: userPhone,
          otp: newOtp,
          purpose: "registration",
          requestId,
          expiresAt: updatedDraft.registrationOtpExpiresAt,
        });
      }

      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
      setTimerActive(true);
      setHasSmsBeenSent(true);
    } catch (e) {
      console.error("Send SMS OTP error", e);
      setError("Unable to send OTP SMS at the moment.");
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleCancel = async () => {
    if (verificationType === "login") {
      clearStoredLoginOtpChallenge();
      localStorage.removeItem("user");
      sessionStorage.removeItem('freshLoginInProgress');
      try {
        await signOut(auth);
      } catch (err) {
        console.error("Sign-out error:", err);
      }
      navigate("/login");
      return;
    }

    // For registration, navigate back to account setup
    const draft = JSON.parse(localStorage.getItem("registrationDraft") || "{}");
    localStorage.removeItem("user");
    clearStoredRegistrationPassword();
    navigate("/account-setup", { state: draft });
  };

  return (
    <>
    <div className="verification-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="verification-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="verification-container">
      <div className="verification-card">
        <h1 className="verification-title">
          {verificationType === "login" 
            ? "Enter Verification Code" 
            : "Verify Your Email"}
        </h1>
        <p className="verification-subtitle">
          Enter 6-digit code we sent to: <strong>{verificationMethod === "sms" ? userPhone : emailDisplay}</strong>
        </p>

        <div className={`verification-otp-inputs ${error ? "shake" : ""}`}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`verification-otp-input ${error && !digit ? "error" : ""}`}
              disabled={isVerifying}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="verification-error-text">{error}</div>}

        {otpExpired && (
          <div className="verification-error-text">Code has expired. Please request a new one.</div>
        )}

        {timerActive && (
          <p className="verification-timer-section">
            Code expires in <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
          </p>
        )}

        {verificationMethod === "email" ? (
          <p className="verification-resend-section">
            {cameFromSmsMethod ? (
              hasEmailBeenSent ? (
                <>Didn't receive a code?{" "}
                <span className={`verification-resend-link ${isSendingVerification ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: isSendingVerification ? "none" : "auto", opacity: isSendingVerification ? 0.5 : 1 }}>{resendText}</span>
              </>
              ) : (
                <span className={`verification-resend-link ${isSendingVerification ? "disabled" : ""}`} onClick={handleSendEmail} style={{ pointerEvents: isSendingVerification ? "none" : "auto", opacity: isSendingVerification ? 0.5 : 1 }}>Send Email</span>
              )
            ) : (
              <>Didn't receive a code?{" "}
              <span className={`verification-resend-link ${isSendingVerification ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: isSendingVerification ? "none" : "auto", opacity: isSendingVerification ? 0.5 : 1 }}>{resendText}</span>
            </>
            )}
          </p>
        ) : (
          <p className="verification-resend-section">
            {hasSmsBeenSent ? (
              <>Didn't receive a code?{" "}
              <span className={`verification-resend-link ${isSendingVerification ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: isSendingVerification ? "none" : "auto", opacity: isSendingVerification ? 0.5 : 1 }}>{resendText}</span></>
            ) : (
              <span className={`verification-resend-link ${isSendingVerification ? "disabled" : ""}`} onClick={handleSendSms} style={{ pointerEvents: isSendingVerification ? "none" : "auto", opacity: isSendingVerification ? 0.5 : 1 }}>Send SMS</span>
            )}
          </p>
        )}

        {verificationMethod === "sms" && (
          <span className="back-link" onClick={handleTryAnotherWayBack} style={{ textDecoration: "underline" }}>
            Try another way
          </span>
        )}

        {verificationMethod === "email" && userPhone && (
          <span className="back-link" onClick={handleTryAnotherWay} style={{ textDecoration: "underline" }}>
            Try another way
          </span>
        )}

        <button className="verification-verify-btn" onClick={handleVerify} disabled={isVerifying || !isOtpComplete || otpExpired}>
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <button
          className="verification-cancel-btn"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>

      <NotificationModal
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
    </>
  );
}
